import { EMainProductCodeType } from '@aqx-core/common/api/config';
import {
  EFillPolicy,
  EOrderBalanceType,
  EOrderDealDirection,
  EOrderStatus,
  EOrderType
} from '@aqx-core/common/api/order';
import { TAppLanguageISOCountryCode, i18n } from '@aqx-core/common/i18n';
import { companyViewController } from '@aqx-core/common/services/company/controller';
import { EUIBuySellType, EUIOrderTypeLabel } from '@aqx-core/common/services/trade/form/types';

import { IMapOrderStatusWithTypeToTextParams } from './types';

export const getOrderBuySellType = (orderType: EOrderType) => {
  switch (orderType) {
    case EOrderType.BUY:
    case EOrderType.BUY_LIMIT:
    case EOrderType.BUY_STOP:
    case EOrderType.BUY_STOP_LIMIT:
    case EOrderType.BUY_MARKET_TO_LIMIT:
      return EUIBuySellType.BUY;
    default:
      return EUIBuySellType.SELL;
  }
};

export const mapOrderTypeToText = (type: EOrderType, suffix?: boolean, lng?: TAppLanguageISOCountryCode) => {
  const ORDER_TYPE_TEXT_MAP: Record<EOrderType, (suffix?: boolean) => string> = {
    [EOrderType.BUY]: (suffix?: boolean) => (suffix ? i18n.t('Buy Order', { lng }) : i18n.t('Buy', { lng })),
    [EOrderType.SELL]: (suffix?: boolean) => (suffix ? i18n.t('Sell Order', { lng }) : i18n.t('Sell', { lng })),
    [EOrderType.BUY_LIMIT]: (suffix?: boolean) =>
      suffix ? i18n.t('Buy Limit Order', { lng }) : i18n.t('Buy Limit', { lng }),
    [EOrderType.SELL_LIMIT]: (suffix?: boolean) =>
      suffix ? i18n.t('Sell Limit Order', { lng }) : i18n.t('Sell Limit', { lng }),
    [EOrderType.BUY_STOP]: (suffix?: boolean) =>
      suffix ? i18n.t('Buy Stop Order', { lng }) : i18n.t('Buy Stop', { lng }),
    [EOrderType.SELL_STOP]: (suffix?: boolean) =>
      suffix ? i18n.t('Sell Stop Order', { lng }) : i18n.t('Sell Stop', { lng }),
    [EOrderType.BALANCE]: () => i18n.t('Balance', { lng }),
    [EOrderType.CREDIT]: () => i18n.t('Credit', { lng }),
    [EOrderType.BUY_STOP_LIMIT]: (suffix?: boolean) =>
      suffix ? i18n.t('Buy Stop Limit Order', { lng }) : i18n.t('Buy Stop Limit', { lng }),
    [EOrderType.SELL_STOP_LIMIT]: (suffix?: boolean) =>
      suffix ? i18n.t('Sell Stop Limit Order', { lng }) : i18n.t('Sell Stop Limit', { lng }),
    [EOrderType.CLOSE_BY]: () => i18n.t('Close By', { lng }),
    [EOrderType.BUY_MARKET_TO_LIMIT]: (suffix?: boolean) =>
      suffix ? i18n.t('Buy Market To Limit Order', { lng }) : i18n.t('Buy Market To Limit', { lng }),
    [EOrderType.SELL_MARKET_TO_LIMIT]: (suffix?: boolean) =>
      suffix ? i18n.t('Sell Market To Limit Order', { lng }) : i18n.t('Sell Market To Limit', { lng })
  };
  if (type === undefined || type === null) {
    return '';
  }
  return ORDER_TYPE_TEXT_MAP[type]?.(suffix) || type.toString();
};

export const mapOrderTypeToTextUpperCase = (type: EOrderType, suffix?: boolean, lng?: TAppLanguageISOCountryCode) => {
  return mapOrderTypeToText(type, suffix, lng).toUpperCase();
};

export const mapOrderBuySellTypeToTextUpperCase = (byeSellType: EUIBuySellType, lng?: TAppLanguageISOCountryCode) => {
  const BUY_SELL_TYPE_LABEL_TEXT_FN_MAP: Record<EUIBuySellType, () => string> = {
    [EUIBuySellType.BUY]: () => i18n.t('BUY', { lng }),
    [EUIBuySellType.SELL]: () => i18n.t('SELL', { lng })
  };
  return BUY_SELL_TYPE_LABEL_TEXT_FN_MAP[byeSellType]();
};

export const mapOrderTypeToOrderTypeLabel = (orderType: EOrderType) => {
  const ORDER_TYPE_LABEL_MAP: Partial<Record<EOrderType, EUIOrderTypeLabel>> = {
    [EOrderType.BUY]: EUIOrderTypeLabel.MARKET,
    [EOrderType.SELL]: EUIOrderTypeLabel.MARKET,
    [EOrderType.BUY_LIMIT]: EUIOrderTypeLabel.LIMIT,
    [EOrderType.SELL_LIMIT]: EUIOrderTypeLabel.LIMIT,
    [EOrderType.BUY_STOP]: EUIOrderTypeLabel.STOP,
    [EOrderType.SELL_STOP]: EUIOrderTypeLabel.STOP,
    [EOrderType.BUY_STOP_LIMIT]: EUIOrderTypeLabel.STOP_LIMIT,
    [EOrderType.SELL_STOP_LIMIT]: EUIOrderTypeLabel.STOP_LIMIT,
    [EOrderType.BUY_MARKET_TO_LIMIT]: EUIOrderTypeLabel.MARKET_TO_LIMIT,
    [EOrderType.SELL_MARKET_TO_LIMIT]: EUIOrderTypeLabel.MARKET_TO_LIMIT
  } as const;
  if (orderType === undefined || orderType === null) {
    return undefined;
  }
  return ORDER_TYPE_LABEL_MAP[orderType];
};

export const mapOrderTypeLabelToText = (orderTypeLabel: EUIOrderTypeLabel, lng?: TAppLanguageISOCountryCode) => {
  const ORDER_TYPE_LABEL_TEXT_MAP: Record<EUIOrderTypeLabel, () => string> = {
    [EUIOrderTypeLabel.LIMIT]: () => i18n.t('Limit', { lng }),
    [EUIOrderTypeLabel.MARKET]: () => i18n.t('Market', { lng }),
    [EUIOrderTypeLabel.MARKET_TO_LIMIT]: () => i18n.t('Market-to-limit', { lng }),
    [EUIOrderTypeLabel.STOP]: () => i18n.t('Stop', { lng }),
    [EUIOrderTypeLabel.STOP_LIMIT]: () => i18n.t('Stop Limit', { lng })
  };
  return ORDER_TYPE_LABEL_TEXT_MAP[orderTypeLabel]();
};

export const mapOrderTypeLabelToDescription = (orderTypeLabel: EUIOrderTypeLabel, lng?: TAppLanguageISOCountryCode) => {
  const ORDER_TYPE_LABEL_DESCRIPTION_MAP: Record<EUIOrderTypeLabel, () => string> = {
    [EUIOrderTypeLabel.MARKET]: () => i18n.t('Executed immediately at the best available market price.', { lng }),
    [EUIOrderTypeLabel.LIMIT]: () => i18n.t('Will be executed at a specified price or better.', { lng }),
    [EUIOrderTypeLabel.STOP]: () => i18n.t('Will be executed at a specified price.', { lng }),
    [EUIOrderTypeLabel.STOP_LIMIT]: () =>
      i18n.t('Will become a limit order that will only be executed at a specified limit price or better.', { lng }),
    [EUIOrderTypeLabel.MARKET_TO_LIMIT]: () =>
      i18n.t('Executed immediately at the best available market price.', { lng })
  };
  return ORDER_TYPE_LABEL_DESCRIPTION_MAP[orderTypeLabel]();
};

export function priceToPoints({
  input,
  price,
  pointStep
}: {
  input: string;
  price: string;
  orderType: string;
  pointStep: number;
}): string {
  // Example logic, adapt as needed
  const diff = Math.abs(Number(input) - Number(price));
  return (diff / pointStep).toFixed(0);
}

export function pointsToPrice({
  input,
  points,
  orderType,
  pointStep
}: {
  input: string;
  points: string;
  orderType: string;
  pointStep: number;
}): string {
  // Example logic, adapt as needed
  const delta = Number(points) * pointStep;
  return orderType === 'BUY' ? (Number(input) + delta).toFixed(2) : (Number(input) - delta).toFixed(2);
}

export const mapOrderStatusToText = (status: EOrderStatus) => {
  const ORDER_STATUS_TEXT_MAP: Record<EOrderStatus, () => string> = {
    [EOrderStatus.CLOSED]: () => i18n.t('Closed'),
    [EOrderStatus.EXPIRED]: () => i18n.t('Expired'),
    [EOrderStatus.DELETED]: () => i18n.t('Canceled'),
    [EOrderStatus.CREDITED]: () => i18n.t('Credited'),
    [EOrderStatus.NEW]: () => i18n.t('New'),
    [EOrderStatus.PARTIALLY_FILLED]: () => i18n.t('Partially Filled'),
    [EOrderStatus.PARTIAL]: () => i18n.t('Partial'),
    [EOrderStatus.FILLED]: () => i18n.t('Filled'),
    [EOrderStatus.DONE_FOR_DAY]: () => i18n.t('Done For Day'),
    [EOrderStatus.CANCELED]: () => i18n.t('Canceled'),
    [EOrderStatus.REPLACED]: () => i18n.t('Replaced'),
    [EOrderStatus.PENDING_CANCEL]: () => i18n.t('Pending Cancel'),
    [EOrderStatus.STOPPED]: () => i18n.t('Stopped'),
    [EOrderStatus.REJECTED]: () => i18n.t('Rejected'),
    [EOrderStatus.SUSPENDED]: () => i18n.t('Suspended'),
    [EOrderStatus.PENDING_NEW]: () => i18n.t('Pending New'),
    [EOrderStatus.CALCULATED]: () => i18n.t('Calculated'),
    [EOrderStatus.ACCEPTED_FOR_BIDDING]: () => i18n.t('Accepted For Bidding'),
    [EOrderStatus.PENDING_REPLACE]: () => i18n.t('Pending Replace'),
    [EOrderStatus.PENDING]: () => i18n.t('Pending')
  };

  return ORDER_STATUS_TEXT_MAP[status]?.() || status;
};

export const mapOrderBalanceTypeToText = (type: EOrderBalanceType) => {
  const ORDER_BALANCE_TYPE_TEXT_MAP: Record<EOrderBalanceType, () => string> = {
    [EOrderBalanceType.OTHERS]: () => i18n.t('Others'),
    [EOrderBalanceType.TOP_UP]: () => i18n.t('Topup'),
    [EOrderBalanceType.DEPOSIT]: () => i18n.t('Deposit'),
    [EOrderBalanceType.WITHDRAWAL]: () => i18n.t('Withdrawal'),
    [EOrderBalanceType.PENALTY_FEE]: () => i18n.t('Penalty'),
    [EOrderBalanceType.UPFRONT_FEE]: () => i18n.t('Upfront'),
    [EOrderBalanceType.SUBSCRIPTION]: () => i18n.t('Subscription'),
    [EOrderBalanceType.UNSUBSCRIPTION]: () => i18n.t('Unsubscription'),
    [EOrderBalanceType.MANAGEMENT_FEE]: () => i18n.t('Management'),
    [EOrderBalanceType.PERFORMANCE_FEE]: () => i18n.t('Performance'),
    [EOrderBalanceType.COMMISSION]: () => i18n.t('Commission'),
    [EOrderBalanceType.SWAPS]: () => i18n.t('Swaps'),
    [EOrderBalanceType.CREDIT]: () => i18n.t('Credit'),
    [EOrderBalanceType.PROFIT_LOSS]: () => i18n.t('Profit/Loss')
  };

  return ORDER_BALANCE_TYPE_TEXT_MAP[type]?.() || String(type);
};

export const mapOrderStatusWithTypeToText = (params: IMapOrderStatusWithTypeToTextParams) => {
  const { status, orderType, orderBalanceType } = params;
  const integration = companyViewController.getCompanyIntegration();

  // Centroid Logic
  if (orderBalanceType && integration === EMainProductCodeType.CENTROID) {
    return mapOrderBalanceTypeToText(orderBalanceType);
  }

  // Metatrader Logic
  if (orderType === EOrderType.BALANCE && orderBalanceType !== undefined) {
    return mapOrderBalanceTypeToText(orderBalanceType);
  }

  return mapOrderStatusToText(status);
};

export const isOrderClosableOrEditable = ({
  marketFillPolicy,
  flag
}: {
  marketFillPolicy: EFillPolicy[];
  flag?: boolean;
}) => {
  const hasValidFlag = flag === true;
  const hasMarketFillPolicies = marketFillPolicy?.length > 0;

  return hasValidFlag && hasMarketFillPolicies;
};

export { calculateLiveProfitForOrder, updateOrdersWithLiveProfits } from './profit';
export { formatExitMethod } from './formatters';

export const isMarketOrder = (orderType?: EOrderType): orderType is EOrderType.BUY | EOrderType.SELL => {
  return orderType === EOrderType.BUY || orderType === EOrderType.SELL;
};

export const isLimitOrStopOrder = (orderType: EOrderType | null | undefined) =>
  orderType === EOrderType.BUY_LIMIT ||
  orderType === EOrderType.SELL_LIMIT ||
  orderType === EOrderType.BUY_STOP ||
  orderType === EOrderType.SELL_STOP;

export const isStopLimitOrder = (orderType: EOrderType | null | undefined) =>
  orderType === EOrderType.BUY_STOP_LIMIT || orderType === EOrderType.SELL_STOP_LIMIT;

export const getDealDirectionText = (direction: EOrderDealDirection) => {
  switch (direction) {
    case EOrderDealDirection.IN:
      return i18n.t('In');
    case EOrderDealDirection.OUT:
      return i18n.t('Out');
    case EOrderDealDirection.INOUT:
      return i18n.t('In/Out');
    case EOrderDealDirection.OUT_BY:
      return i18n.t('Out By');
    default:
      return '';
  }
};

export const getDealDirectionColor = (direction: EOrderDealDirection) => {
  switch (direction) {
    case EOrderDealDirection.IN:
      return 'green';
    case EOrderDealDirection.OUT:
    case EOrderDealDirection.INOUT:
    case EOrderDealDirection.OUT_BY:
    default:
      return 'red';
  }
};
